/* Copyright (c) 2020-present CloudIO Inc */

import type { Attribute, RootRequest } from '@cloudio-saas/datasource-types';
import type { Platform } from 'componentregistry/platform/Platform';
import {
  StateXGetter,
  StateXSetter,
  useStateXValue,
} from '@cloudio-saas/statex';
import { isEmpty } from 'utils/isEmpty';

export const EMPTY_ARRAY = Object.freeze([]);
export const MISSING_STORE_PATH = ['_missing_store'];

export function attributesPath(appUid: string, ds: string) {
  return ['dsCache', appUid, ds, 'attributes'];
}

export function pkAttributesPath(appUid: string, ds: string) {
  return ['dsCache', appUid, ds, 'pkAttributes'];
}

export function useAttributesInitialized(appUid: string, ds: string): boolean {
  return useStateXValue(attributesPath(appUid, ds), undefined) !== undefined;
}

export function useAttributes(appUid: string, ds: string): Attribute[] {
  return useStateXValue(attributesPath(appUid, ds), []);
}

export function usePkAttributes(appUid: string, ds: string): Attribute[] {
  return useStateXValue(pkAttributesPath(appUid, ds), []);
}

class DSAttributesCache {
  set: StateXSetter;

  get: StateXGetter;

  platform: Platform;

  static promiseCache: Map<string, Map<string, Promise<boolean> | boolean>> =
    new Map();

  constructor(props: {
    set: StateXSetter;
    get: StateXGetter;
    platform: Platform;
  }) {
    const { set, get, platform } = props;
    this.set = set;
    this.get = get;
    this.platform = platform;
  }

  static dsList: string[] = [];

  static dsListPromise: Promise<boolean> | null = null;

  static destroy = () => {
    DSAttributesCache.dsListPromise = null;
    DSAttributesCache.promiseCache = new Map();
    DSAttributesCache.dsList = [];
  };

  fetchDSList = async () => {
    if (DSAttributesCache.dsList.length === 0) {
      if (DSAttributesCache.dsListPromise) {
        return DSAttributesCache.dsListPromise;
      }
      DSAttributesCache.dsListPromise = new Promise((resolve, reject) => {
        const key = 'IoCloudIoDatasourceList';
        const reqBody: RootRequest<never> = {
          [key]: {
            ds: 'IoCloudIoDatasourceList',
            query: {
              limit: 2000,
            },
          },
        };
        this.platform
          .sendRequest(
            {
              type: 'Post',
              data: reqBody,
              skipBatchMode: true,
            },
            { appUid: 'cloudio' },
          )[0]
          .then((result) => {
            if (result.status === 'success') {
              const rows: { ds: string }[] = result.data[key].data;
              DSAttributesCache.dsList = rows.map((a) => a.ds);
            }
            resolve(true);
          })
          .catch((e) => {
            reject(e);
          })
          .finally(() => {
            DSAttributesCache.dsListPromise = null;
          });
      });
      return DSAttributesCache.dsListPromise;
    }
    return true;
  };

  static dsAppUidCache: Map<string, string[]> = new Map();

  static getDependentAppUids = (appUid: string, ds: string): string[] => {
    return DSAttributesCache.dsAppUidCache.get(`${ds}-${appUid}`) ?? [];
  };

  fetchAttributes = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
  ) => {
    if (dsAppUid) {
      let dsAppUidList = DSAttributesCache.dsAppUidCache.get(
        `${ds}-${dsAppUid}`,
      );
      if (!dsAppUidList) {
        dsAppUidList = [];
        DSAttributesCache.dsAppUidCache.set(`${ds}-${dsAppUid}`, dsAppUidList);
      }
      if (!dsAppUidList.includes(appUid)) {
        dsAppUidList.push(appUid);
      }
    }
    const key = `${ds}-dsa`;
    const reqBody: RootRequest<Attribute> = {
      [key]: {
        ds: 'IoDatasourceAttributesList',
        query: {
          data: {
            datasourceUid: ds,
            appUid: DSAttributesCache.dsList.includes(ds)
              ? 'cloudio'
              : dsAppUid || appUid,
          },
          limit: 2000,
          sort: { seqNo: 1 },
        },
      },
    };
    const _appUid = dsAppUid || appUid;
    if (isEmpty(_appUid)) {
      throw new Error(
        `AppUid is missing! Not sending request: ${JSON.stringify(reqBody)}`,
      );
    }
    const [promise] = this.platform.sendRequest(
      {
        type: 'Post',
        data: reqBody,
        system: true,
        skipBatchMode: true,
      },
      {
        appUid: _appUid,
        roleUid: this.platform.roleUid,
        pageUid: this.platform.pageId,
      },
    );
    const result = await promise;
    if (result.status === 'success') {
      const attributes: Attribute[] = result.data[key].data;
      this.set(attributesPath(appUid, ds), attributes);
      const pkAttributes = attributes.filter((a) => a.primaryKey === 'Y');
      this.set(pkAttributesPath(appUid, ds), pkAttributes);
    }
  };

  static clear = (appUid: string, ds: string): void => {
    const map = DSAttributesCache.promiseCache.get(appUid);
    if (!map) {
      return;
    }
    const status = map.get(ds);
    if (status === undefined) {
      return;
    }
    map.delete(ds);
  };

  refreshAttributes = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
  ): Promise<boolean> => {
    const map = DSAttributesCache.promiseCache.get(appUid);
    if (!map) {
      // no need to refresh as the app was never initialized
      return true;
    }
    const status = map.get(ds);
    if (status === undefined) {
      // no need to refresh as the data source was never initialized
      return true;
    }
    map.delete(ds);
    return this.initializeAttributes(appUid, ds, dsAppUid);
  };

  static setInitializedAttributes = (appUid: string, ds: string) => {
    let map = DSAttributesCache.promiseCache.get(appUid);
    if (!map) {
      map = new Map();
      DSAttributesCache.promiseCache.set(appUid, map);
    }
    const status = map.get(ds);
    if (status === undefined) {
      map.set(ds, true);
    }
  };

  initializeAttributes = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
    cloudStore?: boolean,
  ): Promise<boolean> => {
    let map = DSAttributesCache.promiseCache.get(appUid);
    if (!map) {
      map = new Map();
      DSAttributesCache.promiseCache.set(appUid, map);
    }
    if (cloudStore) {
      return true;
    }
    let status = map.get(ds);
    if (status !== undefined) {
      return status;
    }
    status = new Promise<boolean>((resolve, reject) => {
      this.fetchDSList()
        .then(async () => {
          await this.fetchAttributes(appUid, ds, dsAppUid);
          resolve(true);
          map?.set(ds, true);
          return true;
        })
        .catch((e) => {
          reject(e);
        });
    });
    map.set(ds, status);
    return status;
  };

  getAttributes = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
    cloudStore?: boolean,
  ): Promise<Attribute[]> => {
    await this.initializeAttributes(appUid, ds, dsAppUid, cloudStore);
    return this.get(attributesPath(appUid, ds)) ?? [];
  };

  getAttribute = (
    appUid: string,
    ds: string,
    attr: string,
  ): Attribute | undefined => {
    return (this.get<Attribute[]>(attributesPath(appUid, ds)) ?? []).find(
      (a) => a.attributeCode === attr,
    );
  };

  getPrimaryKeyAttributes = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
  ): Promise<Attribute[]> => {
    await this.initializeAttributes(appUid, ds, dsAppUid);
    return this.get(pkAttributesPath(appUid, ds)) ?? [];
  };
}

export default DSAttributesCache;
